import { useClinicalEditors } from './context';
import { useEffect, useState } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from '@rudi/ui/button';
import { Checkbox } from '@rudi/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@rudi/ui/dialog';
import { Input } from '@rudi/ui/input';
import { Label } from '@rudi/ui/label';
import { Textarea } from '@rudi/ui/textarea';
import { getErrorMessage } from './context';

import type { ManagedClinicalTrial, ManagedStudyContact } from '@rudi/shared/types/clinical-trials';

interface StudyManagementDialogProps {
  study: ManagedClinicalTrial;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => Promise<void>;
  managementInvestigatorSlug?: string | null;
}

const RECRUITMENT_OPTIONS = [
  ['not_started', 'Noch nicht gestartet'],
  ['recruiting', 'Rekrutierend'],
  ['recruitment_paused', 'Rekrutierung pausiert'],
  ['recruitment_ended', 'Rekrutierung beendet'],
] as const;

function contactForSite(study: ManagedClinicalTrial, siteId: string): ManagedStudyContact | null {
  const siteScoped =
    study.management.contact_scope !== 'study' &&
    (study.source === 'ctis' || study.source === 'ctis_drks');
  if (!siteScoped) return study.contact ?? null;
  const site = (study.centers ?? []).find(center => center.catalog_site_id === siteId);
  return site?.contact ?? null;
}

function sameContact(a: ManagedStudyContact | null, b: ManagedStudyContact | null): boolean {
  return (
    (a?.name ?? '') === (b?.name ?? '') &&
    (a?.role ?? '') === (b?.role ?? '') &&
    (a?.email ?? '') === (b?.email ?? '') &&
    (a?.phone ?? '') === (b?.phone ?? '')
  );
}

export function StudyManagementDialog({
  study,
  open,
  onOpenChange,
  onSaved,
  managementInvestigatorSlug,
}: StudyManagementDialogProps) {
  const {
    t,
    toast,
    operations: {
      updateMedicalRepositoryStudy,
      updateManagedStudyContact,
      updateManagedStudyCriteria,
      updateManagedStudyRecruitment,
      updateManagedStudyShortTitle,
      updateManagedStudyVisibility,
      unassignStudyFromInvestigator,
    },
  } = useClinicalEditors();
  const [inclusion, setInclusion] = useState(study.inclusion_criteria ?? '');
  const [exclusion, setExclusion] = useState(study.exclusion_criteria ?? '');
  const [status, setStatus] = useState(study.recruitment_status_code ?? 'recruiting');
  const allowedSiteIds = study.management.recruitment_site_ids;
  const [siteId, setSiteId] = useState(allowedSiteIds[0] ?? '');
  const [shortTitle, setShortTitle] = useState(study.short_title ?? '');
  const [contactName, setContactName] = useState('');
  const [contactRole, setContactRole] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [published, setPublished] = useState(study.published ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const siteScoped =
    study.management.contact_scope !== 'study' &&
    (study.source === 'ctis' || study.source === 'ctis_drks');
  const canEditDetails = study.management.can_edit_details ?? false;
  const sites = (study.centers ?? []).filter(
    center => center.catalog_site_id && allowedSiteIds.includes(center.catalog_site_id)
  );

  useEffect(() => {
    if (!open) return;
    setInclusion(study.inclusion_criteria ?? '');
    setExclusion(study.exclusion_criteria ?? '');
    setStatus(study.recruitment_status_code ?? '');
    setSiteId(allowedSiteIds[0] ?? '');
    setShortTitle(study.short_title ?? '');
    setPublished(study.published ?? true);
    setError(null);
  }, [allowedSiteIds, open, study]);

  useEffect(() => {
    if (!open) return;
    const contact = contactForSite(study, siteId);
    setContactName(contact?.name ?? '');
    setContactRole(contact?.role ?? '');
    setContactEmail(contact?.email ?? '');
    setContactPhone(contact?.phone ?? '');
  }, [open, siteId, study]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (study.management.provider === 'medical-repository') {
        const fields: Record<string, unknown> = {};
        if ((shortTitle.trim() || null) !== (study.short_title ?? null))
          fields.shortTitle = shortTitle.trim() || null;
        if (status !== study.recruitment_status_code) fields.recruitmentStatus = status || null;
        const contact = contactName.trim()
          ? {
              name: contactName.trim(),
              role: contactRole.trim() || null,
              email: contactEmail.trim() || null,
              phone: contactPhone.trim() || null,
            }
          : null;
        if (!sameContact(contact, study.contact ?? null)) fields.contactPerson = contact;
        if (study.management.can_edit_criteria) {
          const lines = (value: string) =>
            value
              .split('\n')
              .map(line => line.trim())
              .filter(Boolean);
          if (inclusion !== (study.inclusion_criteria ?? ''))
            fields.inclusionCriteria = lines(inclusion);
          if (exclusion !== (study.exclusion_criteria ?? ''))
            fields.exclusionCriteria = lines(exclusion);
        }
        if (published !== (study.published ?? true)) fields.publishedToNetwork = published;
        if (Object.keys(fields).length)
          await updateMedicalRepositoryStudy(study, managementInvestigatorSlug, fields);
        await onSaved();
        onOpenChange(false);
        return;
      }
      let version = study.version;
      const applyVersion = (updated: { version?: number | null }) => {
        version = updated.version ?? version + 1;
      };
      if (study.management.can_edit_criteria) {
        applyVersion(
          await updateManagedStudyCriteria(
            study.catalog_id,
            inclusion || null,
            exclusion || null,
            version,
            managementInvestigatorSlug
          )
        );
      }
      if (canEditDetails && (shortTitle.trim() || null) !== (study.short_title ?? null)) {
        applyVersion(
          await updateManagedStudyShortTitle(
            study.catalog_id,
            shortTitle.trim() || null,
            version,
            managementInvestigatorSlug
          )
        );
      }
      if (canEditDetails && (!siteScoped || siteId)) {
        const nextContact: ManagedStudyContact | null = contactName.trim()
          ? {
              name: contactName.trim(),
              role: contactRole.trim() || null,
              email: contactEmail.trim() || null,
              phone: contactPhone.trim() || null,
            }
          : null;
        if (!sameContact(nextContact, contactForSite(study, siteId))) {
          applyVersion(
            await updateManagedStudyContact(
              study.catalog_id,
              nextContact,
              siteScoped ? siteId : null,
              version,
              managementInvestigatorSlug
            )
          );
        }
      }
      if (canEditDetails && study.source === 'custom' && published !== (study.published ?? true)) {
        applyVersion(
          await updateManagedStudyVisibility(
            study.catalog_id,
            published,
            version,
            managementInvestigatorSlug
          )
        );
      }
      if (study.management.can_edit_recruitment) {
        await updateManagedStudyRecruitment(
          study.catalog_id,
          status,
          siteScoped ? siteId : null,
          version,
          managementInvestigatorSlug
        );
      }
      await onSaved();
      onOpenChange(false);
    } catch (cause) {
      setError(getErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const unassign = async () => {
    if (!managementInvestigatorSlug || !study.management.can_unassign) return;
    const assignmentIds = study.management.assignment_ids ?? [];
    if (assignmentIds.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const assignmentId of assignmentIds) {
        await unassignStudyFromInvestigator(managementInvestigatorSlug, assignmentId);
      }
      await onSaved();
      onOpenChange(false);
      toast({
        title: t('clinicalTrials.ui.assignmentRemoved'),
        description:
          study.management.association_source === 'registry'
            ? t('clinicalTrials.ui.theImmutableRegistryRelationshipIsPreserved')
            : undefined,
      });
    } catch (cause) {
      setError(getErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-3xl">
        <DialogHeader>
          <DialogTitle>{t('clinicalTrials.ui.manageStudy')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {study.management.shared_study_fields && (
            <div className="flex gap-2 rounded-3xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              {t('clinicalTrials.ui.theShortTitleCriteriaAndVisibilityApplyToThe')}
            </div>
          )}
          {canEditDetails && (
            <div>
              <Label htmlFor="managed-study-short-title">
                {t('clinicalTrials.ui.shortTitleAbbreviation')}
              </Label>
              <Input
                id="managed-study-short-title"
                value={shortTitle}
                maxLength={120}
                placeholder={t('clinicalTrials.ui.eGFrexalt')}
                onChange={event => setShortTitle(event.target.value)}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {t('clinicalTrials.ui.shownEverywhereAsAPrefixToTheStudyTitle')}
              </p>
            </div>
          )}
          {study.management.can_edit_criteria && (
            <>
              <div>
                <Label>{t('clinicalTrials.ui.inclusionCriteria')}</Label>
                <Textarea
                  rows={6}
                  value={inclusion}
                  onChange={event => setInclusion(event.target.value)}
                />
              </div>
              <div>
                <Label>{t('clinicalTrials.ui.exclusionCriteria')}</Label>
                <Textarea
                  rows={6}
                  value={exclusion}
                  onChange={event => setExclusion(event.target.value)}
                />
              </div>
            </>
          )}
          {(study.management.can_edit_recruitment || (canEditDetails && siteScoped)) &&
            siteScoped && (
              <div>
                <Label htmlFor="managed-study-site">{t('clinicalTrials.ui.studySite')}</Label>
                <select
                  id="managed-study-site"
                  className="h-10 w-full rounded-3xl border border-input bg-background px-3 text-sm"
                  value={siteId}
                  onChange={event => setSiteId(event.target.value)}
                >
                  {sites.map(center => (
                    <option key={center.catalog_site_id} value={center.catalog_site_id ?? ''}>
                      {[center.organization, center.city].filter(Boolean).join(' · ') ||
                        t('clinicalTrials.ui.studySite')}
                    </option>
                  ))}
                </select>
              </div>
            )}
          {study.management.can_edit_recruitment && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="managed-study-status">
                  {t('clinicalTrials.ui.recruitmentStatus')}
                </Label>
                <select
                  id="managed-study-status"
                  className="h-10 w-full rounded-3xl border border-input bg-background px-3 text-sm"
                  value={status}
                  onChange={event => setStatus(event.target.value)}
                >
                  {RECRUITMENT_OPTIONS.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {canEditDetails && (!siteScoped || sites.length > 0) && (
            <div className="space-y-2 rounded-3xl border p-3">
              <div className="text-sm font-medium">
                {t('clinicalTrials.ui.patientContactPerson')}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="managed-contact-name">Name</Label>
                  <Input
                    id="managed-contact-name"
                    value={contactName}
                    maxLength={255}
                    onChange={event => setContactName(event.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="managed-contact-role">
                    {t('clinicalTrials.ui.rolePosition')}
                  </Label>
                  <Input
                    id="managed-contact-role"
                    value={contactRole}
                    maxLength={255}
                    onChange={event => setContactRole(event.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="managed-contact-email">{t('clinicalTrials.ui.email')}</Label>
                  <Input
                    id="managed-contact-email"
                    type="email"
                    value={contactEmail}
                    maxLength={320}
                    onChange={event => setContactEmail(event.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="managed-contact-phone">{t('clinicalTrials.ui.phone')}</Label>
                  <Input
                    id="managed-contact-phone"
                    value={contactPhone}
                    maxLength={100}
                    onChange={event => setContactPhone(event.target.value)}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('clinicalTrials.ui.anEmptyNameRemovesTheContactPerson')}
              </p>
            </div>
          )}
          {canEditDetails && study.source === 'custom' && (
            <div className="flex items-start gap-2 rounded-3xl border p-3">
              <Checkbox
                id="managed-study-published"
                checked={published}
                onCheckedChange={checked => setPublished(checked === true)}
              />
              <div className="grid gap-0.5 leading-none">
                <Label htmlFor="managed-study-published" className="cursor-pointer">
                  {t('clinicalTrials.ui.listedInTheStudyNetwork')}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t('clinicalTrials.ui.whenUncheckedTheStudyStaysAvailableToTheChatbot')}
                </p>
              </div>
            </div>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          {managementInvestigatorSlug && study.management.can_unassign && (
            <Button variant="destructive" onClick={unassign} disabled={busy} className="mr-auto">
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              {t('clinicalTrials.ui.removeAssignment')}
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {t('clinicalTrials.ui.cancel')}
          </Button>
          <Button
            onClick={save}
            disabled={busy || (siteScoped && study.management.can_edit_recruitment && !siteId)}
          >
            {busy ? t('clinicalTrials.ui.saving') : t('clinicalTrials.ui.saveChanges')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
